import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

/**
 * Endpoints we never attach the auth header to, never gate on token expiry,
 * and never retry on 401. Adding/removing here is the only change needed when
 * login flows evolve.
 *
 * `rest/ssoauth` and `rest/userlogon` are the two session-establishing
 * exchanges and MUST be listed (ADR-0073). They are the calls that *produce* a
 * token, so gating them on the validity of the token they are about to replace
 * is backwards: with an expired session in storage, the proactive gate would
 * fire a Cognito refresh ahead of the very login the user just completed, and
 * a failure there would bounce them back to the IdP instead of signing them
 * in. Note `'user/logon'` does not match `rest/userlogon` — the legacy slug
 * has no slash — so the explicit entry is load-bearing, not a duplicate.
 */
const OPEN_ENDPOINTS = [
  'rest/ssoauth',
  'rest/userlogon',
  'user/logon',
  'rest/verifyuser',
  'rest/user/forgotpassword',
  'rest/user/resetpassword',
];

/** True when this request is the Cognito refresh call itself. */
function isCognitoUrl(url: string): boolean {
  return url.includes('cognito-idp.') && url.includes('.amazonaws.com');
}

function isOpenEndpoint(url: string): boolean {
  return OPEN_ENDPOINTS.some((path) => url.includes(path));
}

/** True for the Portal API channel (`{portalApiUrl}impl/<name>`) — the
 *  legacy `ws.callPortalApiService` transport (Open Banking, KPM2-348). */
function isPortalUrl(url: string): boolean {
  return Boolean(environment.portalApiUrl) && url.startsWith(environment.portalApiUrl);
}

function withAuth(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  // The Portal channel authenticates with a PLAIN lowercase bearer header
  // (`services.js#L290-L305`), not the main API's COG-Authorization
  // `C#~#`-prefixed form — the wrong header 401s every portal call.
  if (isPortalUrl(req.url)) {
    return req.clone({
      setHeaders: { authorization: `Bearer ${token}` },
    });
  }
  return req.clone({
    setHeaders: { 'COG-Authorization': `Bearer C#~#${token}` },
  });
}

/**
 * Session continuity for every outbound call (ADR-0073, KPM2-530).
 *
 * - Skips open endpoints + the Cognito refresh endpoint entirely.
 * - **Proactive (primary):** before attaching, checks the access token's JWT
 *   `exp`. If it has passed — or is within the skew margin — the request
 *   WAITS for a refreshed token and goes out with that one. This restores the
 *   legacy `getToken()` gate (`services.js#L176-L218`) that every legacy
 *   transport sat behind, and it means an expired token is never sent.
 *
 *   That last point is why this is the primary mechanism rather than a
 *   nicety. The reactive path below can only fire on an HTTP 401, and this
 *   backend answers errors as HTTP 200 with a non-zero `errorCode` envelope
 *   (`core/http/kpm-api.types.ts`) — which `KpmApiClient` converts to a
 *   `KpmApiError` before any interceptor sees an `HttpErrorResponse`. Legacy
 *   never established what an expired token actually returns, because it
 *   never sent one (ADR-0073 Context). Not sending one is the fix.
 *
 * - **Reactive (safety net):** on a real 401 — server-side revocation, an
 *   entity switch invalidating the session, a client clock skewed past our
 *   `exp` maths — refresh once via `AuthService.refreshToken$()` and retry
 *   the original request with the new token.
 * - If refresh isn't possible or Cognito rejects the refresh token, the
 *   service clears state and redirects to the IdP, preserving the current
 *   route. We still propagate the original 401 so an in-flight caller that
 *   outlives the redirect sees a definite failure rather than hanging.
 *
 * Attaches `COG-Authorization: Bearer C#~#<token>` (or the plain lowercase
 * `authorization` bearer on the Portal channel — see `withAuth`).
 *
 * No retry recursion: the retried request reuses the original `req`, so it
 * passes through this interceptor again only once. By then the token is
 * freshly minted, so the proactive branch is a no-op; if THAT 401s, refresh
 * itself would 401 next, the service rejects, and the chain terminates.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (isOpenEndpoint(req.url) || isCognitoUrl(req.url)) {
    return next(req);
  }

  const auth = inject(AuthService);
  const token = auth.getToken();
  if (!token) {
    return next(req);
  }

  // Proactive gate, entered only when the cached `exp` comparison says the
  // token is spent — so the common path costs that comparison and nothing
  // else. `ensureFreshToken$` then emits the refreshed token, or hangs
  // without emitting when the session is unrecoverable, which abandons this
  // request quietly while the browser is already bound for the IdP.
  if (auth.isTokenExpired()) {
    return auth
      .ensureFreshToken$()
      .pipe(switchMap((fresh) => sendWith401Retry(req, next, auth, fresh)));
  }

  return sendWith401Retry(req, next, auth, token);
};

/**
 * Send `req` with `token` attached, and on a 401 refresh once and retry.
 * Shared by both entry paths above so the safety net applies whether or not
 * the proactive gate fired.
 */
function sendWith401Retry(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService,
  token: string,
): Observable<HttpEvent<unknown>> {
  return next(withAuth(req, token)).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }
      if (!auth.canRefresh()) {
        // Nothing to refresh with and the server has rejected us: the session
        // is over. Close the dead end that left the app wedged behind a
        // signed-in-looking shell (KPM2-530) by forcing re-auth here.
        auth.forceReauthentication();
        return throwError(() => err);
      }
      return auth.refreshToken$().pipe(
        switchMap((newToken) => next(withAuth(req, newToken))),
        catchError(() => throwError(() => err)),
      );
    }),
  );
}
